// @TODO - This file to be removed when `tmp-use-slsx-session-header` is retired
import React, { FunctionComponent, useEffect, useContext } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import URI from "urijs";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ActionType, MctToastType, UserLanguage } from "../../@types";
import {
  fetchCurrentPlan,
  logError,
  useTranslate,
  isOutsideOpenEnrollment,
  parseSearchParams,
  parseLangFromRelayParam,
} from "../../helpers";
import {
  loginBene,
  getBeneDrugs,
  getBenePharmacies,
  getLoggedInBeneInfo,
} from "../../api";
import { AppContext } from "../../app/store";
import routes from "../../app/routes";
import { AppPage, LoadingMask } from "../../components";
import { fetchPlanIdForNextYear } from "../../helpers/planHelpers";
import { getToastRoute } from "../../helpers";
import {
  hasLoginSession,
  parseDegradedIntegrations,
} from "../../helpers/loginHelpers";
import { getPharmacyTypeFromSavedPharmacies } from "../../helpers/beneficiaryInfoHelpers";

const LoginCallbackHandler: FunctionComponent<RouteComponentProps> = props => {
  const t = useTranslate();
  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  } = useFlags();

  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  // The SLS code, which is transmited to us via query param (aka the 'search' portion of the location object)
  const { search } = props.location;

  const { req_token, relay } = parseSearchParams(URI.parseQuery(search));
  const code = req_token;
  const { state, dispatch } = useContext(AppContext);

  const sendToFromIfExists = () => {
    if (props.history.location.state) {
      const { from } = props.history.location.state as { from?: string };
      from && props.history.replace(from);
    }
  };

  const getBeneficiary = async (): Promise<void> => {
    let userLang = UserLanguage.ENGLISH;
    const needsUserRefresh = hasLoginSession() && !state.beneficiary;

    if (relay) {
      const relayLang = parseLangFromRelayParam(relay);
      userLang = relayLang || userLang;
    }

    if (code || needsUserRefresh) {
      // Clear out any previous bene info, if not already cleared via sessionStorage
      code && dispatch({ type: ActionType.RESET_STATE });

      try {
        const { beneficiary: beneInfo, headers } = code
          ? await loginBene(code)
          : await getLoggedInBeneInfo();

        const degradedIntegrations = parseDegradedIntegrations(headers);

        if (degradedIntegrations.length) {
          dispatch({
            type: ActionType.UPDATE_DEGRADED_INTEGRATIONS,
            payload: degradedIntegrations,
          });
        }

        dispatch({
          type: ActionType.ADD_BENEFICIARY,
          payload: beneInfo,
        });

        dispatch({
          type: ActionType.UPDATE_LIS,
          payload: beneInfo?.lis_level,
        });

        dispatch({
          type: ActionType.UPDATE_FUTURE_LIS,
          payload: beneInfo?.future_lis_level,
        });

        try {
          const drugs = await getBeneDrugs();
          dispatch({ type: ActionType.SET_PRESCRIPTIONS, payload: drugs });
        } catch (e) {
          logError("Failed to get beneficiary drugs (LoginCallbackHandler)", e);
        }

        try {
          const { pharmacies, mail_order } = await getBenePharmacies();
          dispatch({ type: ActionType.SET_PHARMACIES, payload: pharmacies });

          dispatch({
            type: ActionType.UPDATE_PHARMACY_TYPE,
            payload: getPharmacyTypeFromSavedPharmacies(pharmacies, mail_order),
          });
        } catch (e) {
          logError(
            "Failed to get beneficiary pharmacies (LoginCallbackHandler)",
            e
          );
        }

        if (beneInfo?.current_coverage.length) {
          let nextYearPlanId;
          if (!outsideOpenEnrollment || mctYpOeNextYear) {
            nextYearPlanId = await fetchPlanIdForNextYear(
              beneInfo.current_coverage[0],
              "LoginCallbackHandler"
            );
          }
          await fetchCurrentPlan(
            beneInfo,
            state.pharmacies,
            beneInfo.lis_level,
            dispatch,
            nextYearPlanId,
            beneInfo.future_lis_level
          );
        }

        if (needsUserRefresh) {
          sendToFromIfExists();
        } else {
          props.history.replace(`${routes.beneLanding}?lang=${userLang}`);
        }
      } catch (e) {
        logError("Failed to retrieve beneficiary (LoginCallbackHandler)", e);

        if (needsUserRefresh) {
          sendToFromIfExists();
        } else {
          props.history.replace(
            getToastRoute(
              `${routes.questionRouting}?lang=${userLang}`,
              MctToastType.BENE_FAILED_LOGIN
            )
          );
        }
      }
    } else if (state.beneficiary) {
      props.history.replace(`${routes.beneLanding}?lang=${userLang}`);
    } else {
      props.history.replace(
        getToastRoute(
          `${routes.questionRouting}?lang=${userLang}`,
          MctToastType.BENE_FAILED_LOGIN
        )
      );
    }
  };

  useEffect(() => {
    getBeneficiary();
  }, []);

  return (
    <AppPage>
      <LoadingMask
        loading={true}
        message={t("loading.retrieving_your_information")}
      />
    </AppPage>
  );
};

export default withRouter(LoginCallbackHandler);
